import React, { useCallback } from 'react';
import { Button } from '@vibe/core';
import type { UtilizationReportResponse, UtilizationCell, ReportColumn, ReportScope } from '../../api/report.api';
import {
  createWorkbook, addTitleRow, styleHeaderRow, styleGroupRow, styleDataRow, styleTotalRow,
  fillCell, setColumnWidths, downloadWorkbook, getUtilizationColor, round, hexToArgb, COLORS,
} from '../../utils/export-report-excel';

// ─── Compat helpers (old store data may use dateColumns / dailyCells / date) ─

/* eslint-disable @typescript-eslint/no-explicit-any */
function normCell(c: any): UtilizationCell {
  if (c.startDate) return c;
  return { ...c, startDate: c.date, endDate: c.date };
}
function getUtilCells(obj: any): UtilizationCell[] {
  return (obj.cells ?? obj.dailyCells ?? []).map(normCell);
}
function getUtilColumns(data: any): ReportColumn[] {
  if (data.columns) return data.columns;
  return (data.dateColumns ?? []).map((c: any) => ({
    key: c.date, label: String(c.dayOfMonth), startDate: c.date, endDate: c.date, isWeekend: c.isWeekend,
  }));
}
function getUtilScope(data: any): ReportScope { return data.scope ?? 'day'; }
/* eslint-enable @typescript-eslint/no-explicit-any */

interface Props {
  data: UtilizationReportResponse;
}

const SCOPE_FILE_LABELS: Record<string, string> = {
  day: 'daily',
  week: 'weekly',
  month: 'monthly',
};

const ReportExportButton: React.FC<Props> = ({ data }) => {
  const handleExport = useCallback(async () => {
    try {
    const columns = getUtilColumns(data);
    const groups = data.groups;
    const scope = getUtilScope(data);

    const wb = createWorkbook();
    const ws = wb.addWorksheet('Utilization Report');

    // Title
    const scopeLabel = SCOPE_FILE_LABELS[scope] ?? scope;
    addTitleRow(ws, `Utilization Report (${scopeLabel})`, data.period.startDate, data.period.endDate);

    // Header row
    const headers = [
      'Team / Person',
      'Available (h)',
      'Utilized (h)',
      'Utilization %',
      ...columns.map((col) => col.label),
    ];
    const headerRow = ws.addRow(headers);
    styleHeaderRow(ws, headerRow);

    // Highlight weekend columns in header
    for (let i = 0; i < columns.length; i++) {
      if (columns[i].isWeekend) {
        const cell = headerRow.getCell(5 + i);
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4A4A4F' } };
      }
    }

    // Column widths
    setColumnWidths(ws, [
      28, 16, 14, 14,
      ...columns.map(() => 12),
    ]);

    for (const group of groups) {
      // Group header row
      if (groups.length > 1 || group.groupName !== 'All Users') {
        const groupRow = ws.addRow([
          group.groupName,
          round(group.totalAvailableHours),
          round(group.totalScheduledHours),
          round(group.utilizationRate),
          ...columns.map(() => ''),
        ]);
        styleGroupRow(groupRow);
        // Format utilization as %
        const pctCell = groupRow.getCell(4);
        pctCell.numFmt = '0.00';
      }

      // User rows
      for (const row of group.rows) {
        const cellMap = new Map(getUtilCells(row).map((c) => [c.startDate, c]));

        const userRow = ws.addRow([
          row.userName,
          round(row.totalAvailableHours),
          round(row.totalScheduledHours),
          round(row.utilizationRate),
          ...columns.map((col) => {
            const cell = cellMap.get(col.startDate);
            if (!cell) return '';
            if (scope === 'day' && (cell.isWeekend || cell.isHoliday || cell.isLeave)) return '-';
            return round(cell.scheduledHours);
          }),
        ]);
        styleDataRow(userRow);
        userRow.getCell(1).font = { bold: true, size: 10 };
        userRow.getCell(4).numFmt = '0.00';

        // Color the utilization % cell
        const rateColor = getUtilizationColor(row.utilizationRate);
        if (rateColor) {
          fillCell(userRow.getCell(4), rateColor.bg, rateColor.font, true);
        }

        // Color date cells
        for (let i = 0; i < columns.length; i++) {
          const cell = cellMap.get(columns[i].startDate);
          const excelCell = userRow.getCell(5 + i);
          excelCell.alignment = { horizontal: 'center', vertical: 'middle' };

          if (!cell) continue;
          const isOff = scope === 'day' && (cell.isWeekend || cell.isHoliday || cell.isLeave);
          if (isOff) {
            fillCell(excelCell, COLORS.off, COLORS.secondaryText);
            continue;
          }
          if (cell.availableHours > 0) {
            const cellRate = (cell.scheduledHours / cell.availableHours) * 100;
            const color = getUtilizationColor(cellRate);
            if (color) fillCell(excelCell, color.bg, color.font);
          }
        }

        // Board sub-rows
        if (row.boardSubRows) {
          for (const subRow of row.boardSubRows) {
            const subCellMap = new Map(getUtilCells(subRow).map((c) => [c.startDate, c]));
            const subExcelRow = ws.addRow([
              `  ${subRow.boardName}`,
              '',
              round(subRow.totalScheduledHours),
              round(subRow.utilizationRate),
              ...columns.map((col) => {
                const cell = subCellMap.get(col.startDate);
                if (!cell) return '';
                if (scope === 'day' && (cell.isWeekend || cell.isHoliday || cell.isLeave)) return '-';
                return round(cell.scheduledHours);
              }),
            ]);
            styleDataRow(subExcelRow, true);
            subExcelRow.getCell(4).numFmt = '0.00';
            subExcelRow.font = { size: 9, color: { argb: hexToArgb(COLORS.secondaryText) } };
          }
        }

        // Item sub-rows
        if (row.itemSubRows) {
          for (const subRow of row.itemSubRows) {
            const subCellMap = new Map(getUtilCells(subRow).map((c) => [c.startDate, c]));
            const subExcelRow = ws.addRow([
              `  ${subRow.itemName} (${subRow.boardName})`,
              '',
              round(subRow.totalScheduledHours),
              round(subRow.utilizationRate),
              ...columns.map((col) => {
                const cell = subCellMap.get(col.startDate);
                if (!cell) return '';
                if (scope === 'day' && (cell.isWeekend || cell.isHoliday || cell.isLeave)) return '-';
                return round(cell.scheduledHours);
              }),
            ]);
            styleDataRow(subExcelRow, true);
            subExcelRow.getCell(4).numFmt = '0.00';
            subExcelRow.font = { size: 9, color: { argb: hexToArgb(COLORS.secondaryText) } };
          }
        }
      }
    }

    // Summary row
    const { summary } = data;
    const summaryRow = ws.addRow([
      'Total',
      round(summary.totalAvailableHours),
      round(summary.totalScheduledHours),
      round(summary.overallUtilizationRate),
    ]);
    styleTotalRow(summaryRow);
    summaryRow.getCell(4).numFmt = '0.00';

    await downloadWorkbook(wb, `utilization_${scopeLabel}_report_${data.period.startDate}_${data.period.endDate}.xlsx`);
    } catch (err) {
      console.error('Excel export failed:', err);
    }
  }, [data]);

  return (
    <Button
      kind={Button.kinds.SECONDARY}
      size={Button.sizes.SMALL}
      onClick={handleExport}
    >
      Export Excel
    </Button>
  );
};

export default ReportExportButton;